import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          await refreshUser();
          return;
        }
        if (data.status === "expired") { setState("error"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center rounded-2xl border border-white/10 bg-zinc-950/60 p-10" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#00E5FF] animate-spin" />
            <h1 className="font-display text-2xl mt-5">Confirming your payment…</h1>
            <p className="text-white/50 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#39FF14]" data-testid="payment-success-icon" />
            <h1 className="font-display text-2xl mt-5">You're all set! 🎉</h1>
            <p className="text-white/60 mt-2 text-sm">Your plan and credits have been applied to your account.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 h-11 rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium" data-testid="payment-success-continue">
              Back to dashboard
            </Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#FF007F]" />
            <h1 className="font-display text-2xl mt-5">Payment processing</h1>
            <p className="text-white/50 mt-2 text-sm">This is taking a little longer than usual. Your account will update automatically once it clears.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="payment-pending-continue">Back to dashboard</Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-red-400" />
            <h1 className="font-display text-2xl mt-5">Something went wrong</h1>
            <p className="text-white/50 mt-2 text-sm">We couldn't confirm this payment. If you were charged, it'll be reflected shortly.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="payment-error-retry">Back to pricing</Button>
          </>
        )}
      </div>
    </div>
  );
}
